import { useEffect } from "react";
import { useContent, resolveAsset } from "@/context/ContentContext";

/** Set or update a meta tag in <head>. Supports name= and property= meta. */
function setMeta(attr, value, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function absoluteUrl(path) {
  if (!path) return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** SEOHead — dynamically syncs <head> meta tags from CMS-published content.
 *
 *  Props:
 *   - route: "home" | "privacy" | "terms"
 *   - title, description, image, url : optional per-route overrides.
 */
export default function SEOHead({ route = "home", title, description, image, url }) {
  const { content } = useContent() || {};
  const seo = content?.seo || {};
  const hero = content?.hero || {};

  useEffect(() => {
    if (typeof document === "undefined") return;

    // Determine the right defaults for the route
    let defaultTitle = seo.site_title || "Ranjana Baruah";
    let defaultDescription = seo.site_description || hero.kicker || "";
    let defaultImage = seo.og_image || hero.image || "/assets/p-cyber.jpg";

    if (route === "privacy") {
      defaultTitle = seo.privacy_title || "Privacy Policy — Ranjana Baruah";
      defaultDescription = seo.privacy_description || defaultDescription;
    } else if (route === "terms") {
      defaultTitle = seo.terms_title || "Terms & Conditions — Ranjana Baruah";
      defaultDescription = seo.terms_description || defaultDescription;
    }

    const finalTitle = title || defaultTitle;
    const finalDescription = description || defaultDescription;
    const finalImage = absoluteUrl(image || resolveAsset(defaultImage));
    const finalUrl = url || (typeof window !== "undefined" ? window.location.href : "");

    document.title = finalTitle;

    setMeta("name", "description", finalDescription);
    setMeta("name", "keywords", seo.site_keywords || "");
    setMeta("name", "author", "Ranjana Suman Baruah");

    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDescription);
    setMeta("property", "og:image", finalImage);
    setMeta("property", "og:url", finalUrl);
    setMeta("property", "og:type", route === "home" ? "website" : "article");
    setMeta("property", "og:site_name", "Ranjana Baruah");
    setMeta("property", "og:locale", "en_IN");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDescription);
    setMeta("name", "twitter:image", finalImage);
    if (seo.twitter_handle) {
      setMeta("name", "twitter:site", seo.twitter_handle);
      setMeta("name", "twitter:creator", seo.twitter_handle);
    }

    setLink("canonical", finalUrl);
  }, [content, route, title, description, image, url, seo, hero]);

  return null;
}
